import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Edit2, Plus, Radical, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { MapStage } from '../../map/components/MapState'
import { useMapDrawing } from '../../map/hooks/useMapDrawing'

import { SectionCard } from '@/components/SectionCard'
import { FloorSummarySkeleton } from '@/components/skeletons/FloorSummarySkeleton'
import { GatewayDialog } from '@/features/gateways/components/GatewayDialog'
import { useDeleteGateway, useGetGateways } from '@/features/gateways/hooks/useGateway'
import { useMapEntityEditor } from '@/features/map/hooks/useMapEntityEditor'
import { Dot, Shape } from '@/features/map/types/shape'
import { mapDepartmentToShape, mapGatewayToDot } from '@/features/map/utils/mapMappers'
import { DepartmentTrackingDialog } from '@/features/track-location/components/DepartmentTrackingDiallog'
import { GatewayCalibrate } from '@/features/track-location/components/GatewayCalibrate'
import { useTrackingSignalR } from '@/features/track-location/hooks/useTrackingSignalR'
import { useConfirm } from '@/hooks/useConfirm'
import { getMutationOptions } from '@/utils/mutation'
import { useTranslation } from 'react-i18next'
import { DrawType, MapSelection, Mode } from '../../map/types/mapStateProps'
import { useDeleteFloor, useGetFloor } from '../hooks/useFloor'
import { Building } from '../types/building'
import { Floor } from '../types/floor'
import { FloorDialog } from './FloorDialog'

type Props = {
  building: Building
  floors: Floor[]
  canManage?: boolean
  page: 'floor-plan' | 'track-location'
}

export function FloorMap({ building, floors, canManage = false, page }: Props) {
  const { t } = useTranslation()
  const confirm = useConfirm()
  const deleteFloor = useDeleteFloor()
  const deleteGateway = useDeleteGateway()

  const [selectedFloorToEdit, setSelectedFloorToEdit] = useState<Floor | null>(null)
  const [selectedFloorId, setSelectedFloorId] = useState<string | null>(null)
  const [floorOpen, setFloorOpen] = useState(false)

  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string | null>(null)

  const [departmentShape, setDepartmentShape] = useState<Shape | null>(null)
  const [departmentTrackingOpen, setDepartmentTrackingOpen] = useState(false)

  const [selectedGatewayId, setSelectedGatewayId] = useState<string | null>(null)
  const [gatewayOpen, setGatewayOpen] = useState(false)
  const [gatewayCalibrateOpen, setGatewayCalibrateOpen] = useState(false)
  const [selectedBLEGatewayDot, setSelectedBLEGatewayDot] = useState<Dot | null>(null)

  const [mode, setMode] = useState<Mode>('view')
  const [drawType, setDrawType] = useState<DrawType>('dot')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [hoveredId, setHoveredId] = useState<string | null>(null)

  const [isMapLoading, setIsMapLoading] = useState(false)

  const selectedFloor = floors.find(f => f.id === selectedFloorId)

  const { data: floor, isFetching: isFetchingFloor } = useGetFloor(building.id, selectedFloor?.id!, !!selectedFloor)

  const { data: gateways, isFetching: isFetchingGateways } = useGetGateways(
    { floorId: selectedFloor?.id },
    !!selectedFloor,
  )

  const departments = floor?.departments
  const width = 600
  const height = 400
  const { dots, setDots, handleSaveDot, handleEditDot, handleCancelDot } = useMapEntityEditor({})
  const { point, handleClick, undo, resetPoints, finishDotDrawing } = useMapDrawing(width, height, mode, drawType)

  const { tags, deviceDots } = useTrackingSignalR({
    floorId: 'floor14',
    gatewayDots: dots,
  })

  const isFloorMap = page === 'floor-plan'
  const isTrackLocation = page === 'track-location'

  useEffect(() => {
    if (!building || floors.length === 0) {
      setSelectedFloorId(null)
    } else {
      const buildingFloors = floors.filter(f => f.buildingId === building.id).sort((a, b) => a.index - b.index)

      if (buildingFloors.length > 0) {
        setSelectedFloorId(buildingFloors[0].id)
      }
    }
  }, [building, floors])

  useEffect(() => {
    if (!floor) return
    setSelectedFloorToEdit(floor)
  }, [floor])

  const departmentShapes: Shape[] = useMemo(() => {
    if (!departments) return []

    return departments.map(d => mapDepartmentToShape(d, 'red')).filter(Boolean) as Shape[]
  }, [departments])

  const summary = useMemo(() => {
    return departments?.reduce(
      (acc, d) => {
        acc.total += d.itemSummary?.total ?? 0
        acc.available += d.itemSummary?.available ?? 0
        acc.inUse += d.itemSummary?.inUse ?? 0
        return acc
      },
      { total: 0, available: 0, inUse: 0 },
    )
  }, [departments])

  useEffect(() => {
    if (!gateways) return

    const mapped: Dot[] = gateways
      .map(g =>
        mapGatewayToDot(g, {
          BLE: 'yellow',
          RFID: 'blue',
        }),
      )
      .filter(Boolean) as Dot[]

    setDots(mapped)
  }, [gateways])

  useEffect(() => {
    setSelectedId(null)
  }, [departments])

  const handleSelect = (selection: MapSelection | null) => {
    if (!selection) {
      setSelectedId(null)
      setSelectedGatewayId(null)
      setSelectedBLEGatewayDot(null)
      return
    }

    if (selection.entity === 'shape' && selection.entityType === 'department') {
      setSelectedId(selection.id)
      setSelectedDepartmentId(selection.id)
      const shape = departmentShapes?.find(d => d.id === selection.id) ?? null
      setDepartmentShape(shape)
      setDepartmentTrackingOpen(true)
    }

    if (selection.entity === 'dot') {
      setSelectedGatewayId(selection.id)
      setSelectedId(selection.id)

      const gateway = dots.find(d => d.id === selection.id && d.dotType === 'gateway' && d.gatewayType === 'BLE')
      if (gateway) {
        setSelectedBLEGatewayDot(gateway)
        setSelectedDepartmentId(gateway.departmentId ?? null)
        const shape = departmentShapes.find(d => d.id === gateway.departmentId) ?? null
        setDepartmentShape(shape)
      } else {
        setSelectedBLEGatewayDot(null)
      }
    }
  }

  const handleHover = (selection: MapSelection | null) => {
    if (!selection) {
      setHoveredId(null)
      return
    }

    if (selection.entity === 'shape' && selection.entityType === 'department') {
      setHoveredId(selection.id)
    }

    if (selection.entity === 'dot') {
      setHoveredId(selection.id)
    }
  }

  const handleDeleteFloor = async () => {
    if (!floor) return
    const confirmed = await confirm({
      variant: 'destructive',
      title: t('common.deleteConfirmTitle'),
      description: t('floorplan.floor.deleteConfirmDescription', { name: floor?.name }),
    })

    if (confirmed) {
      const options = getMutationOptions({
        successMessage: t('floorplan.floor.toast.deleteSuccess'),
      })

      deleteFloor.mutate({ buildingId: building.id, floorId: floor.id }, options)
    }
  }

  const handleDeleteGateway = async (gatewayId: string) => {
    const name = gateways?.find(g => g.id === gatewayId)?.name
    const confirmed = await confirm({
      variant: 'destructive',
      title: t('common.deleteConfirmTitle'),
      description: t('gateway.deleteConfirmDescription', { name }),
    })

    if (confirmed) {
      const options = getMutationOptions({
        successMessage: t('gateway.toast.deleteGatewaySuccess'),
        onSuccess: () => {
          setSelectedId(null)
          setSelectedGatewayId(null)
        },
      })

      deleteGateway.mutate(gatewayId, options)
    }
  }

  const saveDot = () => {
    const result = finishDotDrawing()
    if (!result) return

    const name = gateways?.find(g => g.id === selectedGatewayId)?.name
    handleSaveDot(result, 'gateway', name)
    setMode('view')

    setGatewayOpen(true)
  }

  const editDot = (id: string) => {
    handleEditDot(id)
    resetPoints()
    setMode('draw')
  }

  const cancelDot = () => {
    handleCancelDot()
    resetPoints()
    setMode('view')
  }

  return (
    <div className="flex flex-col gap-4 lg:flex-row">
      {/* LEFT: MAP */}
      <div className="flex-1">
        <SectionCard
          title={
            floor
              ? t('floorplan.floor.titleWithFloor', {
                  buildingName: building.name,
                  floorName: floor.name,
                })
              : t('floorplan.floor.title', {
                  buildingName: building.name,
                })
          }
          right={
            <>
              {floors
                .filter(f => f.buildingId === building?.id)
                .map(f => (
                  <Button
                    data-testid="floor-button"
                    data-floor-id={f.id}
                    key={f.id}
                    size="sm"
                    variant={floor?.id === f.id ? 'default' : 'outline'}
                    disabled={mode !== 'view'}
                    onClick={() => {
                      setSelectedFloorId(f.id)
                      setSelectedDepartmentId(null)
                      setSelectedGatewayId(null)
                      setSelectedBLEGatewayDot(null)
                    }}
                  >
                    {f.name ? f.name : f.index}
                  </Button>
                ))}
            </>
          }
          subtitleClass="justify-end"
          subtitle={
            <>
              {isFloorMap && canManage && mode === 'view' && (
                <>
                  <Button
                    data-testid="floor-add-button"
                    size="sm"
                    className="h-8 text-xs"
                    disabled={isFetchingFloor}
                    onClick={() => {
                      setSelectedFloorToEdit(null)
                      setFloorOpen(true)
                    }}
                  >
                    <Plus className="size-3" />
                    {t('floorplan.floor.add')}
                  </Button>

                  {floor && (
                    <>
                      <Button
                        data-testid="floor-edit-button"
                        size="sm"
                        className="h-8 text-xs"
                        disabled={isFetchingFloor}
                        onClick={() => {
                          setSelectedFloorToEdit(floor)
                          setFloorOpen(true)
                        }}
                      >
                        <Edit2 className="size-3" />
                        {t('floorplan.floor.edit')}
                      </Button>

                      <Button
                        data-testid="floor-delete-button"
                        size="sm"
                        variant={'default'}
                        className="h-8 text-xs bg-destructive hover:bg-destructive/70"
                        disabled={isFetchingFloor}
                        onClick={() => {
                          handleDeleteFloor()
                        }}
                      >
                        <Trash2 className="size-3" />
                        {t('common.delete')}
                      </Button>
                    </>
                  )}
                </>
              )}

              {floor && isTrackLocation && canManage && mode === 'view' && (
                <>
                  <Button
                    data-testid="gateway-add-location-button"
                    size="sm"
                    className="h-8 text-xs"
                    disabled={isFetchingGateways || isMapLoading}
                    onClick={() => {
                      setSelectedId(null)
                      setSelectedGatewayId(null)
                      setMode('draw')
                    }}
                  >
                    <Plus className="size-3" />
                    {t('common.addLocation')}
                  </Button>

                  {selectedGatewayId && (
                    <>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button data-testid="gateway-edit-button" size="sm" className="h-8 text-xs" disabled={isFetchingGateways}>
                            <Edit2 className="size-3" />
                            {t('common.edit')}
                          </Button>
                        </DropdownMenuTrigger>

                        <DropdownMenuContent align="start" className="w-40">
                          <DropdownMenuItem
                            data-testid="gateway-edit-location"
                            onClick={() => {
                              editDot(selectedGatewayId)
                            }}
                          >
                            {t('common.editLocation')}
                          </DropdownMenuItem>

                          <DropdownMenuItem
                            data-testid="gateway-edit-detail"
                            onClick={() => {
                              setSelectedGatewayId(selectedGatewayId)
                              setGatewayOpen(true)
                            }}
                          >
                            {t('common.editDetail')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>

                      <Button
                        data-testid="gateway-calibrate-button"
                        size="sm"
                        className="h-8 text-xs"
                        disabled={isFetchingGateways || !selectedBLEGatewayDot}
                        onClick={() => {
                          setGatewayCalibrateOpen(true)
                        }}
                      >
                        <Radical className="size-3" />
                        {t('gateway.calibrate')}
                      </Button>

                      <Button
                        data-testid="gateway-delete-button"
                        size="sm"
                        variant={'default'}
                        className="h-8 text-xs bg-destructive hover:bg-destructive/70"
                        disabled={isFetchingGateways}
                        onClick={() => {
                          handleDeleteGateway(selectedGatewayId)
                        }}
                      >
                        <Trash2 className="size-3" />
                        {t('common.delete')}
                      </Button>
                    </>
                  )}
                </>
              )}

              {floor && isTrackLocation && canManage && mode === 'draw' && (
                <div className="flex gap-2">
                  <Button data-testid="gateway-draw-undo" size="sm" variant="outline" onClick={() => undo()} disabled={!point} className="text-xs">
                    {t('common.undo')}
                  </Button>
                  <Button
                    data-testid="gateway-draw-cancel"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      cancelDot()
                    }}
                    className="text-xs"
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button data-testid="gateway-draw-save" size="sm" onClick={() => saveDot()} className="text-xs">
                    {t('common.save')}
                  </Button>
                </div>
              )}
            </>
          }
        >
          <MapStage
            imageId={floor?.imageId ?? ''}
            width={width}
            height={height}
            point={point}
            shapes={departmentShapes}
            dots={dots}
            mode={mode}
            drawType={drawType}
            selectedId={selectedId}
            hoveredId={hoveredId}
            allowDotInteraction={!isFloorMap}
            isLoading={isFetchingFloor}
            onMapClick={handleClick}
            onSelect={handleSelect}
            onHover={handleHover}
            onMapLoadingChange={setIsMapLoading}
          />
        </SectionCard>
      </div>

      {/* Side Panel */}
      <div className="w-full lg:w-72">
        <div className="flex flex-col gap-4">
          {/* Floor Info */}

          {isFetchingFloor ? (
            <FloorSummarySkeleton />
          ) : (
            <SectionCard title={t('floorplan.departmentSummaryTitle', { floorName: floor?.name })} noCardTitle>
              <div className="mt-3 flex flex-col gap-2">
                <div className="flex items-center justify-between text-xs gap-2">
                  <span className="text-muted-foreground">{t('common.department')}</span>
                  <span className="font-medium text-foreground text-right">
                    {departments?.map(d => d.name).join(', ') || '-'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">{t('floorplan.allEquipment')}</span>
                  <span className="font-medium text-foreground">{summary?.total}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">{t('floorplan.available')}</span>
                  <span className="font-medium text-success">{summary?.available}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">{t('common.inUse')}</span>
                  <span className="font-medium text-info">{summary?.inUse}</span>
                </div>
              </div>
            </SectionCard>
          )}
        </div>
      </div>

      <FloorDialog
        open={floorOpen}
        onOpenChange={setFloorOpen}
        buildingId={building.id}
        floor={selectedFloorToEdit}
      ></FloorDialog>

      {floor && (
        <GatewayDialog
          open={gatewayOpen}
          onOpenChange={open => {
            setGatewayOpen(open)

            if (!open) {
              cancelDot()
            }
          }}
          gatewayId={selectedGatewayId ?? null}
          floorId={floor.id}
          point={point}
        ></GatewayDialog>
      )}

      {departmentTrackingOpen && selectedDepartmentId && departmentShape && (
        <DepartmentTrackingDialog
          open={departmentTrackingOpen}
          onOpenChange={open => {
            setDepartmentTrackingOpen(open)

            if (!open) {
              setSelectedId(null)
              setSelectedDepartmentId(null)
              setDepartmentShape(null)
            }
          }}
          departmentId={selectedDepartmentId}
          departmentShape={departmentShape}
          gatewayDots={dots.filter(d => d.departmentId === selectedDepartmentId)}
          deviceDots={deviceDots.filter(d => d.departmentId === selectedDepartmentId)}
        ></DepartmentTrackingDialog>
      )}

      {gatewayCalibrateOpen && selectedBLEGatewayDot && (
        <GatewayCalibrate
          open={gatewayCalibrateOpen}
          onOpenChange={open => {
            setGatewayCalibrateOpen(open)

            if (!open) {
              setSelectedId(null)
              setSelectedDepartmentId(null)
              setSelectedGatewayId(null)
              setSelectedBLEGatewayDot(null)
            }
          }}
          departmentId={selectedDepartmentId}
          gatewayDot={selectedBLEGatewayDot}
          deviceDots={deviceDots.filter(
            d => d.departmentId === selectedDepartmentId && d.gatewayId === selectedBLEGatewayDot.id,
          )}
          tags={tags}
        />
      )}
    </div>
  )
}
